import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, MapPin, Crown, Play, Trophy } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { GROUPS } from "@/data/mockData";
import { weeklyCount } from "@/lib/streak";
import { toast } from "sonner";

const NAME_POOL = [
  "QuizWizKaz", "BrainyBev", "PintSizedPro", "LagerLuke", "TriviaTina",
  "NachoNerd", "AleAce", "CleverClogs", "TapRoomTom", "RiddleRita",
  "SavvySue", "BarflyBen", "GuvnorGaz", "SnugSam", "PubPaula", "Craftycarl",
];

function seededRandom(seed) {
  let x = 0;
  for (const c of seed) x = (x * 31 + c.charCodeAt(0)) % 2147483647;
  return () => {
    x = (x * 1103515245 + 12345) % 2147483648;
    return x / 2147483648;
  };
}

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, joinGroup } = useApp();
  const group = GROUPS.find((g) => g.id === id);

  const joined = group ? profile?.groups?.includes(group.name) : false;

  const board = useMemo(() => {
    if (!group) return [];
    const rand = seededRandom(group.id);
    const n = Math.min(NAME_POOL.length, Math.max(4, Math.round(group.members / 4)));
    const pool = [...NAME_POOL];
    const rows = [];
    for (let i = 0; i < n && pool.length; i++) {
      const idx = Math.floor(rand() * pool.length);
      const name = pool.splice(idx, 1)[0];
      rows.push({ name, points: Math.round((rand() * 900 + 120) / 10) * 10, isYou: false });
    }
    if (joined) {
      const myPoints = Math.max(100, weeklyCount(profile.history) * 100 + Math.round(seededRandom(profile.handle)() * 400));
      rows.push({ name: profile.name, points: myPoints, isYou: true });
    }
    rows.sort((a, b) => b.points - a.points);
    return rows;
  }, [group, joined, profile]);

  if (!group) {
    return (
      <div className="p-6 pt-16 text-center">
        <p className="text-slate-400 mb-4">This group doesn't exist.</p>
        <button data-testid="group-notfound-home" onClick={() => navigate("/search?tab=groups")} className="bg-pink-500 text-white font-bold rounded-xl px-6 py-3">Browse groups</button>
      </div>
    );
  }

  const handleJoin = () => {
    joinGroup(group.name);
    toast.success(joined ? `You're already in ${group.name}!` : `You joined ${group.name}! 🎉`);
  };

  const myRank = board.findIndex((r) => r.isYou) + 1;

  return (
    <div className="p-6 pb-10" data-testid="group-detail">
      <div className="flex items-center gap-3 mb-6 pt-4">
        <button data-testid="group-back-btn" onClick={() => navigate("/search?tab=groups")} className="text-slate-400 p-1">
          <ArrowLeft strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-2xl font-black text-slate-50">Group</h1>
      </div>

      <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 flex items-center gap-4 mb-6">
        <div className="text-5xl">{group.emoji}</div>
        <div className="flex-1">
          <h2 data-testid="group-name" className="font-display font-black text-2xl text-slate-50 leading-none">{group.name}</h2>
          <p className="text-slate-400 text-sm mt-1">{group.vibe}</p>
          <p className="text-slate-500 text-xs mt-2 flex items-center gap-3">
            <span className="flex items-center gap-1"><Users size={12} /> {group.members} members</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {group.distance} mi</span>
          </p>
        </div>
      </div>

      <div className="flex gap-3 mb-8">
        <motion.button
          data-testid="group-join-btn"
          whileTap={{ scale: 0.96 }}
          onClick={handleJoin}
          disabled={joined}
          className={`flex-1 h-14 rounded-2xl font-display font-black flex items-center justify-center gap-2 no-tap-highlight ${joined ? "bg-slate-800 text-green-400" : "bg-pink-500 text-white shadow-[0_0_20px_rgba(236,72,153,0.35)]"}`}
        >
          <Users size={20} strokeWidth={2.5} /> {joined ? "Joined ✓" : "Join Group"}
        </motion.button>
        <motion.button
          data-testid="group-play-btn"
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate(`/setup?venue=${encodeURIComponent(group.name + " — Group Night")}`)}
          className="flex-1 h-14 bg-amber-400 text-slate-950 rounded-2xl font-display font-black flex items-center justify-center gap-2 no-tap-highlight"
        >
          <Play size={20} strokeWidth={2.5} fill="currentColor" /> Play
        </motion.button>
      </div>

      <h2 className="font-display text-xl font-black text-slate-50 mb-1 flex items-center gap-2">
        <Trophy size={20} className="text-amber-400" /> This Week's Leaderboard
      </h2>
      <p className="text-slate-500 text-xs mb-4">Resets every Saturday 10pm. {joined ? `You're #${myRank}.` : "Join to appear on the board!"}</p>

      <div className="space-y-3" data-testid="group-leaderboard">
        {board.map((r, i) => (
          <motion.div
            key={r.name + i}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            data-testid={r.isYou ? "group-leaderboard-you" : `group-leaderboard-row-${i}`}
            className={`flex items-center gap-3 rounded-2xl p-3 border ${r.isYou ? "bg-pink-500/10 border-pink-400/40" : "bg-slate-900 border-slate-800"}`}
          >
            <span className={`font-display font-black w-7 text-center ${i === 0 ? "text-amber-400" : "text-slate-500"}`}>
              {i === 0 ? <Crown size={18} className="mx-auto" /> : i + 1}
            </span>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm ${r.isYou ? "bg-pink-500 text-white" : "bg-slate-700 text-slate-300"}`}>
              {r.name[0].toUpperCase()}
            </div>
            <span className={`flex-1 font-bold truncate ${r.isYou ? "text-pink-300" : "text-slate-200"}`}>
              {r.name}{r.isYou && " (You)"}
            </span>
            <span className="font-display font-black text-slate-50">{r.points}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
